import React, { useEffect, useState } from "react";
import { Hash, Plus, Trash2, Loader2, ChevronDown, Check } from "lucide-react";
import { adminApi } from "../../lib/api";
import { useToast } from "../../hooks/use-toast";

// Compact dropdown to pick a saved hashtag set (inserts into the composer) and
// manage sets (create / delete). Sets are shared across all admins.
export default function HashtagSetsPicker({ onInsert }) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [sets, setSets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [tags, setTags] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    adminApi.metricool.hashtagSets().then(setSets).catch(() => setSets([])).finally(() => setLoading(false));
  };
  useEffect(() => { if (open) load(); }, [open]);

  const save = async () => {
    if (!name.trim() || !tags.trim()) { toast({ title: "Add a name and some hashtags", variant: "destructive" }); return; }
    setSaving(true);
    try {
      await adminApi.metricool.saveHashtagSet({ name: name.trim(), hashtags: tags.trim() });
      setName(""); setTags(""); setAdding(false);
      load();
      toast({ title: "Hashtag set saved" });
    } catch (e) {
      toast({ title: "Couldn't save the set", description: e?.response?.data?.detail || "Please try again.", variant: "destructive" });
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await adminApi.metricool.removeHashtagSet(id); load(); }
    catch { toast({ title: "Couldn't delete the set", variant: "destructive" }); }
  };

  const pick = (s) => { onInsert(s.hashtags); setOpen(false); toast({ title: "Hashtags added", description: s.name }); };

  return (
    <div className="relative">
      <button
        type="button"
        data-testid="hashtag-sets-toggle"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 text-xs font-bold text-navy hover:text-orange"
      >
        <Hash size={13} /> Hashtag sets <ChevronDown size={12} className={open ? "rotate-180 transition-transform" : "transition-transform"} />
      </button>

      {open && (
        <div data-testid="hashtag-sets-panel" className="absolute z-30 right-0 mt-2 w-72 bg-white rounded-xl border border-slate-200 shadow-xl p-2">
          {loading ? (
            <div className="py-6 text-center text-slate-400"><Loader2 size={18} className="animate-spin mx-auto" /></div>
          ) : (
            <>
              <div className="max-h-56 overflow-y-auto space-y-1">
                {sets.length === 0 && <p className="text-xs text-slate-400 px-2 py-3 text-center">No saved sets yet.</p>}
                {sets.map((s) => (
                  <div key={s.id} data-testid={`hashtag-set-${s.id}`} className="group flex items-start gap-2 rounded-lg hover:bg-slate-50 px-2 py-1.5">
                    <button type="button" onClick={() => pick(s)} className="flex-1 min-w-0 text-left" data-testid={`hashtag-set-insert-${s.id}`}>
                      <p className="text-xs font-bold text-navy truncate">{s.name}</p>
                      <p className="text-[11px] text-slate-400 truncate">{s.hashtags}</p>
                    </button>
                    <button type="button" onClick={() => remove(s.id)} data-testid={`hashtag-set-delete-${s.id}`} className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 shrink-0 mt-0.5" title="Delete set"><Trash2 size={13} /></button>
                  </div>
                ))}
              </div>

              <div className="border-t border-slate-100 mt-2 pt-2">
                {adding ? (
                  <div className="space-y-2">
                    <input data-testid="hashtag-set-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Set name (e.g. Small biz)" className="w-full text-xs rounded-lg border border-slate-200 px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-orange/40" />
                    <input data-testid="hashtag-set-tags" value={tags} onChange={(e) => setTags(e.target.value)} placeholder="#accounting #smallbusiness" className="w-full text-xs rounded-lg border border-slate-200 px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-orange/40" />
                    <div className="flex gap-2">
                      <button type="button" onClick={save} disabled={saving} data-testid="hashtag-set-save" className="flex-1 inline-flex items-center justify-center gap-1 text-xs font-bold bg-navy text-white rounded-full py-1.5 disabled:opacity-50">{saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Save</button>
                      <button type="button" onClick={() => setAdding(false)} className="text-xs font-semibold text-slate-500 px-3">Cancel</button>
                    </div>
                  </div>
                ) : (
                  <button type="button" onClick={() => setAdding(true)} data-testid="hashtag-set-add" className="w-full inline-flex items-center justify-center gap-1.5 text-xs font-bold text-orange hover:text-orange-dark py-1.5"><Plus size={13} /> New hashtag set</button>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
